import { Breadcrumbs, FormControl, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import z from "zod";
import { onError } from "@/components/core/display-error";
import { ErrorDisplay } from "@/components/core/error-display";
import { TabsComponent } from "@/components/core/tabs-component";
import { Link } from "@/components/core/tanstack-link";
import { WorkflowActionsTable } from "@/components/tables/workflow-actions";
import { WorkflowEnrollment } from "@/components/workflow/enrollment";
import { WorkflowPolicies } from "@/components/workflow/policies";
import { WorkflowSettings } from "@/components/workflow/settings";
import { workflowStateToLabel } from "@/helpers/labels";
import { useTrovePlan } from "@/hooks/trove-plan";
import {
  ActionStatus,
  getEmailGroups,
  getGetEmailGroupsQueryKey,
  getGetRuleFieldsQueryKey,
  getGetRulesQueryKey,
  getGetWorkflowActionsQueryKey,
  getGetWorkflowFeesSettingsQueryKey,
  getGetWorkflowQueryKey,
  getGetWorkflowsQueryKey,
  getRuleFields,
  getRules,
  getWorkflow,
  getWorkflowActions,
  getWorkflowFeesSettings,
  PricingStatusStatus,
  Provider,
  useGetAccount,
  useGetWorkflow,
  useUpdateWorkflow,
  WorkflowState,
} from "@/models/backend";
import { AxiosError, isAxiosError } from "axios";
import { queryClient } from "@/helpers/tanstack-client";

const tabs = ["schedule", "enrollment", "policies", "settings"] as const;

const searchSchema = z.object({
  tab: z.enum(tabs).default("schedule").catch("schedule"),
  editingAction: z.uuid().optional(),
  template: z.enum(ActionStatus).optional(),
});

export const Route = createFileRoute("/_authenticated/_contained/workflows/$workflowId")({
  component: RouteComponent,
  validateSearch: searchSchema,
  loaderDeps: ({ search: { tab } }) => ({ tab }),
  loader: async ({ params: { workflowId }, deps: { tab } }) => {
    try {
      await Promise.all([
        queryClient.ensureQueryData({
          queryFn: (opts) => getWorkflow(workflowId, opts),
          queryKey: getGetWorkflowQueryKey(workflowId),
        }),
        ...(tab == "schedule"
          ? [
              queryClient.ensureQueryData({
                queryFn: (opts) => getWorkflowActions(workflowId, opts),
                queryKey: getGetWorkflowActionsQueryKey(workflowId),
              }),
              queryClient.ensureQueryData({
                queryFn: (opts) => getWorkflowFeesSettings({ workflow_id: workflowId }, opts),
                queryKey: getGetWorkflowFeesSettingsQueryKey({ workflow_id: workflowId }),
              }),
              queryClient.ensureQueryData({
                queryFn: getEmailGroups,
                queryKey: getGetEmailGroupsQueryKey(),
              }),
            ]
          : []),
        ...(tab == "settings"
          ? [
              queryClient.ensureQueryData({
                queryFn: (opts) => getRules(workflowId, opts),
                queryKey: getGetRulesQueryKey(workflowId),
              }),
              queryClient.ensureQueryData({
                queryFn: (opts) => getRuleFields({ workflow_id: workflowId }, opts),
                queryKey: getGetRuleFieldsQueryKey({ workflow_id: workflowId }),
              }),
            ]
          : []),
      ]);
    } catch (e) {
      if (isAxiosError(e) && e.status == 404) {
        throw notFound();
      } else {
        throw e;
      }
    }
  },
});

function RouteComponent() {
  const { status } = useTrovePlan();
  const { tab } = Route.useSearch();
  const { workflowId } = Route.useParams();
  const { data: workflow, isLoading, isError, error, refetch } = useGetWorkflow(workflowId);
  const { mutate: changeWorkflowState, isPending: isChangingWorkflowState } = useUpdateWorkflow();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { data: account } = useGetAccount();
  const accountType = account?.provider;
  if (isError) {
    if (error instanceof AxiosError && error.status == 404) {
      throw notFound();
    }
    return (
      <Stack spacing={4}>
        <ErrorDisplay error={error} onRetry={refetch} title="Failed to load workflow" />
      </Stack>
    );
  }
  if (!workflow || isLoading) return null;

  const selectWorkflowState = (state: WorkflowState) => {
    changeWorkflowState(
      { data: { id: workflow.id, state } },
      {
        onSuccess: () => {
          enqueueSnackbar("Updated workflow state", { variant: "success" });
          queryClient.invalidateQueries({
            queryKey: getGetWorkflowQueryKey(workflow.id),
          });
          queryClient.invalidateQueries({
            queryKey: getGetWorkflowsQueryKey(),
          });
        },
        onError: onError({ prefix: "Couldn't change workflow state" }),
      },
    );
  };

  const tabObjs = [
    {
      id: tabs[0],
      label: "Schedule",
      component: <WorkflowActionsTable workflow={workflow} />,
    },
    {
      id: tabs[1],
      label: "Enrolled invoices",
      component: <WorkflowEnrollment workflow={workflow} />,
    },
    ...(([Provider.xero] as (Provider | undefined)[]).includes(accountType)
      ? [
          {
            id: tabs[2],
            label: "Policies",
            component: <WorkflowPolicies workflow={workflow} />,
          },
        ]
      : []),
    {
      id: tabs[3],
      label: "Settings",
      component: <WorkflowSettings workflow={workflow} />,
    },
  ];

  return (
    <Stack spacing={4}>
      <Breadcrumbs separator=">" aria-label="breadcrumb">
        <Link to="/workflows">Workflows</Link>
        <Typography>{workflow.name}</Typography>
      </Breadcrumbs>
      <Stack spacing={1} direction="row" sx={{ alignItems: "start" }}>
        <Stack>
          <Typography variant="h3">{workflow.name}</Typography>
          <Typography variant="subtitle1">{workflow.description}</Typography>
        </Stack>
        <Stack
          direction="row"
          spacing={1}
          sx={{ ml: "auto", textWrapMode: "nowrap", flexWrap: "wrap" }}
        >
          <FormControl>
            <Select
              error={workflow.state === WorkflowState.disabled}
              size="small"
              sx={{ minWidth: "300px" }}
              disabled={isChangingWorkflowState}
              value={workflow.state}
              onChange={(e) => selectWorkflowState(e.target.value)}
            >
              <MenuItem
                disabled={status === PricingStatusStatus.ERROR}
                value={WorkflowState.sending}
              >
                <Stack>
                  <Typography>{workflowStateToLabel(WorkflowState.sending)}</Typography>
                  <Typography variant="caption">
                    Emails will send automatically on schedule
                  </Typography>
                </Stack>
              </MenuItem>
              <MenuItem value={WorkflowState.manual}>
                <Stack>
                  <Typography>{workflowStateToLabel(WorkflowState.manual)}</Typography>
                  <Typography variant="caption">Click send on every email</Typography>
                </Stack>
              </MenuItem>
              <MenuItem value={WorkflowState.disabled}>
                <Stack>
                  <Typography>{workflowStateToLabel(WorkflowState.disabled)}</Typography>
                  <Typography variant="caption">No emails will be sent</Typography>
                </Stack>
              </MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Stack>
      <Stack spacing={1}>
        <TabsComponent
          tabs={tabObjs}
          selectedTab={tabObjs.findIndex((tabObj) => tabObj.id === tab)}
          onSelectedTab={(idx) => {
            navigate({ search: { tab: tabObjs[idx].id } });
          }}
        />
      </Stack>
    </Stack>
  );
}
